import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { HistoryAction, HistoryEntityType, ProjectHistoryEntry } from '../../../types/projectHistory';

const PAGE_SIZE = 50;

export interface ProjectHistoryFilters {
  entityType?: HistoryEntityType | 'all';
  action?: HistoryAction | 'all';
  startDate?: string | null;
  endDate?: string | null;
  search?: string;
}

interface UseProjectHistoryReturn {
  entries: ProjectHistoryEntry[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

// PostgREST's .or() filter syntax treats "," "(" ")" as structural — strip them
// from free-text search input instead of trying to escape them.
const sanitizeSearchTerm = (term: string): string => term.replace(/[,()]/g, '').trim();

export function useProjectHistory(
  orgId: string | null,
  projectId: string | null,
  filters: ProjectHistoryFilters = {},
): UseProjectHistoryReturn {
  const { entityType = 'all', action = 'all', startDate = null, endDate = null, search = '' } = filters;
  const [entries, setEntries] = useState<ProjectHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const loadPage = useCallback(async (offset: number) => {
    if (!orgId || !projectId) return;
    setLoading(true);
    setError(null);
    try {
      let query = supabase.from('project_history').select('*').eq('project_id', projectId);
      if (entityType !== 'all') query = query.eq('entity_type', entityType);
      if (action !== 'all') query = query.eq('action', action);
      if (startDate) query = query.gte('created_at', `${startDate}T00:00:00`);
      if (endDate) query = query.lte('created_at', `${endDate}T23:59:59`);
      const term = sanitizeSearchTerm(search);
      if (term) query = query.or(`entity_name.ilike.%${term}%,description.ilike.%${term}%`);

      const { data, error: err } = await query
        .order('created_at', { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);
      if (err) throw err;
      const page = (data ?? []) as ProjectHistoryEntry[];
      setEntries((prev) => (offset === 0 ? page : [...prev, ...page]));
      setHasMore(page.length === PAGE_SIZE);
    } catch {
      setError('Er ging iets mis bij het laden van de geschiedenis.');
    } finally {
      setLoading(false);
    }
  }, [orgId, projectId, entityType, action, startDate, endDate, search]);

  useEffect(() => {
    setEntries([]);
    setHasMore(false);
    loadPage(0);
  }, [loadPage]);

  const loadMore = async () => {
    await loadPage(entries.length);
  };

  return { entries, loading, error, hasMore, loadMore };
}
